import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { initializeApp, deleteApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, sendPasswordResetEmail, signOut } from "firebase/auth";
import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc, getFirestore } from "firebase/firestore";
import { auth, db } from "./firebase";
import { germanStates, stateList } from "./germanyData";
import { useLanguage } from "./LanguageContext";
import NotificationBell from "./NotificationBell";
import { sendNotification } from "./notifications";

const firebaseConfig = {
  apiKey: "AIzaSyD4UK5ojjAj_kKyIjXTvkRUNCzLUnng0u0",
  authDomain: "klinik-begleiter24.firebaseapp.com",
  projectId: "klinik-begleiter24",
  storageBucket: "klinik-begleiter24.firebasestorage.app",
  messagingSenderId: "358390061140",
  appId: "1:358390061140:web:022c8a55d95d58622d75c1",
  measurementId: "G-D6P0WKCQSS"
};

function ProviderDashboard({ userData, providerData, onProviderUpdate }) {
  const [activeTab, setActiveTab] = useState("home");
  const [profileSection, setProfileSection] = useState(null);
  const [bookingFilter, setBookingFilter] = useState("confirmed");
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [patientContactMap, setPatientContactMap] = useState({});
  const [bio, setBio] = useState(providerData.bio || "");
  const [qualification, setQualification] = useState(providerData.qualification || "nursing_student");
  const [state, setState] = useState(providerData.state || stateList[0]);
  const [city, setCity] = useState(providerData.city || germanStates[stateList[0]][0]);
  const [phone, setPhone] = useState(providerData.phone || "");
  const [website, setWebsite] = useState(providerData.website || "");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [empName, setEmpName] = useState("");
  const [empEmail, setEmpEmail] = useState("");
  const [empPhone, setEmpPhone] = useState("");
  const [empQualification, setEmpQualification] = useState("nursing_student");
  const [addingEmployee, setAddingEmployee] = useState(false);
  const [empError, setEmpError] = useState("");
  const [assigningRequestId, setAssigningRequestId] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date().toISOString().split("T")[0]);
  const [allEmployeeBookings, setAllEmployeeBookings] = useState([]);
  const navigate = useNavigate();
  const { lang, toggleLang, t } = useLanguage();

  const isAgency = providerData.role === "agency";

  useEffect(() => {
    if (activeTab === "requests") { loadRequests(); if (isAgency) { loadEmployees(); loadAllEmployeeBookings(); } }
    if (activeTab === "bookings") loadBookings();
    if (activeTab === "profile" && isAgency && (profileSection === "team" || profileSection === "calendar")) {
      loadEmployees();
      loadAllEmployeeBookings();
    }
  }, [activeTab, profileSection]);

  const loadRequests = async () => {
    setLoadingRequests(true);
    try {
      const user = auth.currentUser;
      const q = query(collection(db, "bookings"), where("providerId", "==", user.uid), where("status", "==", "requested"));
      const snap = await getDocs(q);
      const results = [];
      snap.forEach((d) => results.push({ id: d.id, ...d.data() }));
      setRequests(results);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRequests(false);
    }
  };

  const loadBookings = async () => {
    setLoadingBookings(true);
    try {
      const user = auth.currentUser;
      const q = query(collection(db, "bookings"), where("providerId", "==", user.uid));
      const snap = await getDocs(q);
      const results = [];
      snap.forEach((d) => results.push({ id: d.id, ...d.data() }));
      setMyBookings(results);
      loadPatientContacts(results);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBookings(false);
    }
  };

  const loadPatientContacts = async (bookingsList) => {
    const newMap = {};
    for (const b of bookingsList) {
      if (b.status !== "confirmed" && b.status !== "completed") continue;
      if (!b.patientId) continue;
      const snap = await getDoc(doc(db, "patientProfiles", b.patientId));
      if (snap.exists()) {
        newMap[b.id] = { photoUrl: snap.data().photoUrl, mobile: snap.data().mobile };
      }
    }
    setPatientContactMap(newMap);
  };

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const user = auth.currentUser;
      const q = query(collection(db, "employees"), where("agencyId", "==", user.uid));
      const snap = await getDocs(q);
      const results = [];
      snap.forEach((d) => results.push({ id: d.id, ...d.data() }));
      setEmployees(results);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const loadAllEmployeeBookings = async () => {
    try {
      const user = auth.currentUser;
      const q = query(collection(db, "bookings"), where("providerId", "==", user.uid), where("status", "==", "confirmed"));
      const snap = await getDocs(q);
      const results = [];
      snap.forEach((d) => results.push({ id: d.id, ...d.data() }));
      setAllEmployeeBookings(results);
    } catch (err) {
      console.error(err);
    }
  };

  const generateRandomPassword = () => {
    return Math.random().toString(36).slice(-10) + Math.random().toString(36).slice(-2).toUpperCase() + "1!";
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setEmpError("");
    setAddingEmployee(true);

    const secondaryAppName = "secondary-" + Date.now();
    let secondaryApp;
    try {
      const owner = auth.currentUser;
      secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
      const secondaryAuth = getAuth(secondaryApp);
      const randomPassword = generateRandomPassword();

      const cred = await createUserWithEmailAndPassword(secondaryAuth, empEmail, randomPassword);
      const newEmployeeUid = cred.user.uid;
      const secondaryDb = getFirestore(secondaryApp);

      await setDoc(doc(secondaryDb, "employees", newEmployeeUid), {
        uid: newEmployeeUid,
        agencyId: owner.uid,
        name: empName,
        email: empEmail,
        phone: empPhone,
        qualification: empQualification,
        unavailableDates: [],
        createdAt: new Date().toISOString(),
      });

      await setDoc(doc(secondaryDb, "users", newEmployeeUid), {
        name: empName,
        email: empEmail,
        role: "employee",
        agencyId: owner.uid,
        verificationStatus: "n/a",
        createdAt: new Date().toISOString(),
      });

      await sendPasswordResetEmail(auth, empEmail);
      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);

      setEmpName("");
      setEmpEmail("");
      setEmpPhone("");
      setEmpQualification("nursing_student");
      loadEmployees();
    } catch (err) {
      setEmpError(err.message);
      if (secondaryApp) {
        try { await deleteApp(secondaryApp); } catch (e) {}
      }
    } finally {
      setAddingEmployee(false);
    }
  };

  const getEmployeeStatusForDate = (employee, date) => {
    if (employee.unavailableDates && employee.unavailableDates.includes(date)) {
      return { status: "unavailable" };
    }
    const assignedBooking = allEmployeeBookings.find((b) => b.employeeId === employee.id && b.date === date);
    if (assignedBooking) {
      return { status: "assigned", patientName: assignedBooking.patientName };
    }
    return { status: "free" };
  };

  const assignEmployeeToRequest = async (bookingId, employee, bookingDate, patientId) => {
    const statusInfo = getEmployeeStatusForDate(employee, bookingDate);
    if (statusInfo.status !== "free") {
      alert(lang === "de" ? "Dieser Mitarbeiter ist an diesem Datum nicht frei." : "This employee is not free on this date.");
      return;
    }
    try {
      await updateDoc(doc(db, "bookings", bookingId), {
        status: "confirmed",
        employeeId: employee.id,
        employeeName: employee.name,
      });
      if (patientId) {
        await sendNotification(
          patientId,
          "Ihr Termin am " + bookingDate + " wurde bestatigt.",
          "Your appointment on " + bookingDate + " has been confirmed."
        );
      }
      await sendNotification(
        employee.id,
        "Ihnen wurde ein neuer Termin am " + bookingDate + " zugewiesen.",
        "You have been assigned a new job on " + bookingDate + "."
      );
      setRequests(requests.filter((r) => r.id !== bookingId));
      setAssigningRequestId(null);
      loadAllEmployeeBookings();
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const markBookingCompleted = async (bookingId, patientId, bookingDate) => {
    try {
      await updateDoc(doc(db, "bookings", bookingId), { status: "completed" });
      if (patientId) {
        await sendNotification(
          patientId,
          "Ihr Termin am " + bookingDate + " wurde als abgeschlossen markiert. Bitte bewerten Sie den Service.",
          "Your appointment on " + bookingDate + " has been marked as completed. Please rate the service."
        );
      }
      loadBookings();
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const cancelBooking = async (booking) => {
    if (!window.confirm(lang === "de" ? "Termin wirklich stornieren?" : "Really cancel this appointment?")) return;
    try {
      await updateDoc(doc(db, "bookings", booking.id), { status: "cancelled" });
      if (booking.patientId) {
        await sendNotification(
          booking.patientId,
          "Ihr Termin am " + booking.date + " wurde storniert.",
          "Your appointment on " + booking.date + " has been cancelled."
        );
      }
      loadBookings();
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const respondToRequest = async (bookingId, newStatus, bookingDate, patientId) => {
    try {
      if (newStatus === "confirmed" && !isAgency) {
        const user = auth.currentUser;
        const conflictQuery = query(
          collection(db, "bookings"),
          where("providerId", "==", user.uid),
          where("status", "==", "confirmed"),
          where("date", "==", bookingDate)
        );
        const conflictSnap = await getDocs(conflictQuery);
        if (!conflictSnap.empty) {
          alert(lang === "de"
            ? "Sie haben an diesem Datum bereits einen bestatigten Termin."
            : "You already have a confirmed appointment on this date.");
          return;
        }
      }
      await updateDoc(doc(db, "bookings", bookingId), { status: newStatus });
      if (patientId) {
        const msgDe = newStatus === "confirmed"
          ? "Ihr Termin am " + bookingDate + " wurde bestatigt."
          : "Ihre Anfrage fur " + bookingDate + " wurde abgelehnt.";
        const msgEn = newStatus === "confirmed"
          ? "Your appointment on " + bookingDate + " has been confirmed."
          : "Your request for " + bookingDate + " has been declined.";
        await sendNotification(patientId, msgDe, msgEn);
      }
      setRequests(requests.filter((r) => r.id !== bookingId));
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const handleStateChange = (newState) => {
    setState(newState);
    setCity(germanStates[newState][0]);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const user = auth.currentUser;
      await setDoc(doc(db, "providers", user.uid), {
        ...providerData,
        bio: bio,
        qualification: providerData.role === "individual" ? qualification : "agency",
        state: state,
        city: city,
        phone: phone,
        website: providerData.role === "agency" ? website : "",
      }, { merge: true });
      setMessage(t("saveProfile") + " OK");
      if (onProviderUpdate) onProviderUpdate();
    } catch (err) {
      setMessage("Error: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const statusLabel = (status) => {
    const map = {
      pending: lang === "de" ? "In Prufung" : "Under Review",
      verified: lang === "de" ? "Verifiziert" : "Verified",
      rejected: lang === "de" ? "Abgelehnt" : "Rejected",
    };
    return map[status] || status;
  };

  const qualificationLabel = (q) => {
    const map = {
      nursing_student: lang === "de" ? "Pflege-/Medizinstudent" : "Nursing/Medical Student",
      retired_nurse: lang === "de" ? "Ehemalige Pflegekraft" : "Retired Nurse",
      alltagsbegleiter: lang === "de" ? "Zertifizierter Alltagsbegleiter" : "Certified Companion",
      other: lang === "de" ? "Sonstige" : "Other",
    };
    return map[q] || q;
  };

  const supportTypeLabel = (type) => {
    if (type === "pickup_dropoff") return lang === "de" ? "Abholung, Begleitung und Nachhausebringen" : "Pickup, escort and drop-off";
    if (type === "meet_only") return lang === "de" ? "Nur Treffen in der Klinik" : "Meet at hospital only";
    return type;
  };

  const employeeStatusLabel = (status) => {
    if (status === "free") return lang === "de" ? "Frei" : "Free";
    if (status === "assigned") return lang === "de" ? "Zugewiesen" : "Assigned";
    if (status === "unavailable") return lang === "de" ? "Nicht verfugbar" : "Unavailable";
    return status;
  };

  const bookingsForTab = myBookings.filter((b) => b.status === bookingFilter);

  return (
    <div style={styles.pageBackground}>
      <div style={styles.phoneFrame}>
        <div style={styles.header}>
          <div>
            <div style={styles.headerTitle}>{t("welcome")}</div>
            <div style={styles.headerSubtitle}>{userData.name}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <NotificationBell />
            <button onClick={toggleLang} style={styles.langToggle}>
              {lang === "de" ? "EN" : "DE"}
            </button>
            <button onClick={handleLogout} style={styles.logoutBtn}>{t("logout")}</button>
          </div>
        </div>

        <div style={styles.content}>
          {activeTab === "home" && (
            <div>
              <div style={{
                ...styles.noticeCard,
                background: providerData.verificationStatus === "verified" ? "#e8f5e9" : "#fff4e5",
                border: providerData.verificationStatus === "verified" ? "1px solid #a5d6a7" : "1px solid #ffd591",
              }}>
                <div style={{
                  ...styles.noticeTitle,
                  color: providerData.verificationStatus === "verified" ? "#256029" : "#874d00",
                }}>
                  {lang === "de" ? "Verifizierungsstatus" : "Verification Status"}: {statusLabel(providerData.verificationStatus)}
                </div>
              </div>

              <div style={styles.sectionLabel}>{t("overview")}</div>
              <div style={styles.grid}>
                <div style={styles.statCard}>
                  <div style={styles.statNumber}>{requests.length}</div>
                  <div style={styles.statLabel}>{lang === "de" ? "Offene Anfragen" : "Open Requests"}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statNumber}>{myBookings.filter(b => b.status === "confirmed").length}</div>
                  <div style={styles.statLabel}>{t("confirmedAppointments")}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statNumber}>{myBookings.filter(b => b.status === "completed").length}</div>
                  <div style={styles.statLabel}>{t("completed")}</div>
                </div>
                {isAgency && (
                  <div style={styles.statCard}>
                    <div style={styles.statNumber}>{employees.length}</div>
                    <div style={styles.statLabel}>{lang === "de" ? "Mitarbeiter" : "Employees"}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "requests" && (
            <div>
              {loadingRequests && <p style={{ textAlign: "center", color: "#999" }}>{t("loading")}</p>}
              {!loadingRequests && requests.length === 0 && (
                <div style={styles.emptyState}>
                  <p>{lang === "de" ? "Keine offenen Anfragen." : "No open requests."}</p>
                </div>
              )}
              {requests.map((r) => (
                <div key={r.id} style={styles.providerCard}>
                  <div style={styles.providerType}>{r.patientName}</div>
                  <div style={styles.providerArea}>{r.hospital}</div>
                  <div style={styles.providerArea}>{r.date} - {r.time}</div>
                  <p style={styles.providerBio}>{supportTypeLabel(r.supportType)}</p>
                  {r.notes && <p style={styles.providerBio}>{r.notes}</p>}

                  {!isAgency && (
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button onClick={() => respondToRequest(r.id, "confirmed", r.date, r.patientId)} style={styles.acceptButton}>
                        {lang === "de" ? "Annehmen" : "Accept"}
                      </button>
                      <button onClick={() => respondToRequest(r.id, "declined", r.date, r.patientId)} style={styles.declineButton}>
                        {lang === "de" ? "Ablehnen" : "Decline"}
                      </button>
                    </div>
                  )}

                  {isAgency && assigningRequestId !== r.id && (
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button onClick={() => { setAssigningRequestId(r.id); loadEmployees(); loadAllEmployeeBookings(); }} style={styles.acceptButton}>
                        {lang === "de" ? "Mitarbeiter zuweisen" : "Assign Employee"}
                      </button>
                      <button onClick={() => respondToRequest(r.id, "declined", r.date, r.patientId)} style={styles.declineButton}>
                        {lang === "de" ? "Ablehnen" : "Decline"}
                      </button>
                    </div>
                  )}

                  {isAgency && assigningRequestId === r.id && (
                    <div style={{ marginTop: 12 }}>
                      <div style={styles.sectionLabel}>{lang === "de" ? "Mitarbeiter fur " + r.date : "Employees for " + r.date}</div>
                      {employees.length === 0 && (
                        <p style={{ fontSize: 12, color: "#999" }}>
                          {lang === "de" ? "Noch keine Mitarbeiter hinzugefugt." : "No employees added yet."}
                        </p>
                      )}
                      {employees.map((emp) => {
                        const statusInfo = getEmployeeStatusForDate(emp, r.date);
                        return (
                          <div key={emp.id} style={styles.employeeRow}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{emp.name}</div>
                              <div style={{ fontSize: 11, color: "#888" }}>{employeeStatusLabel(statusInfo.status)}</div>
                            </div>
                            <button
                              disabled={statusInfo.status !== "free"}
                              onClick={() => assignEmployeeToRequest(r.id, emp, r.date, r.patientId)}
                              style={statusInfo.status === "free" ? styles.assignButton : styles.assignButtonDisabled}
                            >
                              {lang === "de" ? "Zuweisen" : "Assign"}
                            </button>
                          </div>
                        );
                      })}
                      <button onClick={() => setAssigningRequestId(null)} style={styles.backLink}>
                        <span>&#8592;</span> {lang === "de" ? "Abbrechen" : "Cancel"}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === "bookings" && (
            <div>
              <div style={styles.pillRow}>
                <button
                  onClick={() => setBookingFilter("confirmed")}
                  style={bookingFilter === "confirmed" ? styles.pillActive : styles.pill}
                >
                  {lang === "de" ? "Bevorstehend" : "Upcoming"}
                </button>
                <button
                  onClick={() => setBookingFilter("completed")}
                  style={bookingFilter === "completed" ? styles.pillActive : styles.pill}
                >
                  {t("completed")}
                </button>
              </div>

              {loadingBookings && <p style={{ textAlign: "center", color: "#999" }}>{t("loading")}</p>}

              {!loadingBookings && bookingsForTab.length === 0 && (
                <div style={styles.emptyState}>
                  <p>{lang === "de" ? "Keine Termine." : "No appointments."}</p>
                </div>
              )}

              {bookingsForTab.map((b) => {
                const patientContact = patientContactMap[b.id];
                return (
                  <div key={b.id} style={styles.providerCard}>
                    <div style={styles.providerType}>{b.patientName}</div>
                    <div style={styles.providerArea}>{b.hospital}</div>
                    <div style={styles.providerArea}>{b.date} - {b.time}</div>
                    {b.employeeName && <div style={styles.providerArea}>{lang === "de" ? "Mitarbeiter" : "Employee"}: {b.employeeName}</div>}

                    {patientContact && (
                      <div style={styles.patientContactBox}>
                        {patientContact.photoUrl && (
                          <img src={patientContact.photoUrl} alt="Patient" style={styles.patientPhoto} />
                        )}
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 12 }}>
                            {lang === "de" ? "Patient zur Erkennung" : "Patient for recognition"}
                          </div>
                          {patientContact.mobile && (
                            <div style={{ fontSize: 12, color: "#555" }}>{patientContact.mobile}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {!isAgency && bookingFilter === "confirmed" && (
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        <button onClick={() => markBookingCompleted(b.id, b.patientId, b.date)} style={styles.acceptButton}>
                          {lang === "de" ? "Als abgeschlossen markieren" : "Mark as Completed"}
                        </button>
                        <button onClick={() => cancelBooking(b)} style={styles.declineButton}>
                          {lang === "de" ? "Stornieren" : "Cancel"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "profile" && isAgency && !profileSection && (
            <div>
              <div style={styles.menuCard} onClick={() => setProfileSection("about")}>
                <div style={styles.menuTitle}>{lang === "de" ? "Uber mich" : "About me"}</div>
                <div style={styles.menuArrow}>{">"}</div>
              </div>
              <div style={styles.menuCard} onClick={() => setProfileSection("team")}>
                <div style={styles.menuTitle}>{lang === "de" ? "Team" : "Team"}</div>
                <div style={styles.menuArrow}>{">"}</div>
              </div>
              <div style={styles.menuCard} onClick={() => setProfileSection("calendar")}>
                <div style={styles.menuTitle}>{lang === "de" ? "Kalender" : "Calendar"}</div>
                <div style={styles.menuArrow}>{">"}</div>
              </div>
            </div>
          )}

          {activeTab === "profile" && (!isAgency || profileSection === "about") && (
            <div>
              {isAgency && (
                <button onClick={() => setProfileSection(null)} style={styles.backLink}>
                  <span>&#8592;</span> {lang === "de" ? "Zuruck" : "Back"}
                </button>
              )}
              <form onSubmit={handleSaveProfile}>
                <div style={styles.field}>
                  <label style={styles.label}>{lang === "de" ? "Uber mich / unser Unternehmen" : "About me / our company"}</label>
                  <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={4} style={styles.input} />
                </div>

                {providerData.role === "individual" && (
                  <div style={styles.field}>
                    <label style={styles.label}>{lang === "de" ? "Qualifikation" : "Qualification"}</label>
                    <select value={qualification} onChange={(e) => setQualification(e.target.value)} style={styles.input}>
                      <option value="nursing_student">{lang === "de" ? "Pflege-/Medizinstudent" : "Nursing/Medical Student"}</option>
                      <option value="retired_nurse">{lang === "de" ? "Ehemalige Pflegekraft" : "Retired Nurse"}</option>
                      <option value="alltagsbegleiter">{lang === "de" ? "Zertifizierter Alltagsbegleiter" : "Certified Companion"}</option>
                      <option value="other">{lang === "de" ? "Sonstige" : "Other"}</option>
                    </select>
                  </div>
                )}

                <div style={styles.field}>
                  <label style={styles.label}>{lang === "de" ? "Telefonnummer" : "Phone Number"}</label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} style={styles.input} />
                </div>

                {providerData.role === "agency" && (
                  <div style={styles.field}>
                    <label style={styles.label}>{lang === "de" ? "Webseite" : "Website"}</label>
                    <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" style={styles.input} />
                  </div>
                )}

                <div style={styles.field}>
                  <label style={styles.label}>{lang === "de" ? "Bundesland" : "State"}</label>
                  <select value={state} onChange={(e) => handleStateChange(e.target.value)} style={styles.input}>
                    {stateList.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>{lang === "de" ? "Stadt / Bezirk" : "City / District"}</label>
                  <select value={city} onChange={(e) => setCity(e.target.value)} style={styles.input}>
                    {germanStates[state].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>{lang === "de" ? "Verifizierungsstatus" : "Verification Status"}</label>
                  <input value={statusLabel(providerData.verificationStatus)} disabled style={{ ...styles.input, background: "#f5f5f5" }} />
                </div>

                {message && <p style={styles.message}>{message}</p>}

                <button type="submit" disabled={saving} style={styles.button}>
                  {saving ? t("saving") : t("saveProfile")}
                </button>
              </form>
            </div>
          )}

          {activeTab === "profile" && isAgency && profileSection === "team" && (
            <div>
              <button onClick={() => { setProfileSection(null); setSelectedEmployee(null); }} style={styles.backLink}>
                <span>&#8592;</span> {lang === "de" ? "Zuruck" : "Back"}
              </button>

              {!selectedEmployee && (
                <>
                  <form onSubmit={handleAddEmployee} style={{ marginBottom: 20 }}>
                    <div style={styles.field}>
                      <label style={styles.label}>{t("name")}</label>
                      <input value={empName} onChange={(e) => setEmpName(e.target.value)} required style={styles.input} />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>{t("email")}</label>
                      <input type="email" value={empEmail} onChange={(e) => setEmpEmail(e.target.value)} required style={styles.input} />
                    </div>
                    <p style={styles.hint}>
                      {lang === "de"
                        ? "Der Mitarbeiter erhalt eine E-Mail zum Festlegen seines Passworts."
                        : "The employee will receive an email to set their password."}
                    </p>
                    <div style={styles.field}>
                      <label style={styles.label}>{lang === "de" ? "Telefonnummer" : "Phone Number"}</label>
                      <input value={empPhone} onChange={(e) => setEmpPhone(e.target.value)} style={styles.input} />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>{lang === "de" ? "Qualifikation" : "Qualification"}</label>
                      <select value={empQualification} onChange={(e) => setEmpQualification(e.target.value)} style={styles.input}>
                        <option value="nursing_student">{lang === "de" ? "Pflege-/Medizinstudent" : "Nursing/Medical Student"}</option>
                        <option value="retired_nurse">{lang === "de" ? "Ehemalige Pflegekraft" : "Retired Nurse"}</option>
                        <option value="alltagsbegleiter">{lang === "de" ? "Zertifizierter Alltagsbegleiter" : "Certified Companion"}</option>
                        <option value="other">{lang === "de" ? "Sonstige" : "Other"}</option>
                      </select>
                    </div>
                    {empError && <p style={styles.error}>{empError}</p>}
                    <button type="submit" disabled={addingEmployee} style={styles.button}>
                      {addingEmployee ? t("saving") : (lang === "de" ? "Mitarbeiter hinzufugen" : "Add Employee")}
                    </button>
                  </form>

                  {loadingEmployees && <p style={{ textAlign: "center", color: "#999" }}>{t("loading")}</p>}
                  {!loadingEmployees && employees.length === 0 && (
                    <div style={styles.emptyState}>
                      <p>{lang === "de" ? "Noch keine Mitarbeiter." : "No employees yet."}</p>
                    </div>
                  )}
                  {employees.map((emp) => (
                    <div key={emp.id} style={styles.providerCard} onClick={() => setSelectedEmployee(emp)}>
                      <div style={styles.providerType}>{emp.name}</div>
                      <div style={styles.providerArea}>{qualificationLabel(emp.qualification)}</div>
                      <div style={styles.providerArea}>{emp.email} - {emp.phone}</div>
                    </div>
                  ))}
                </>
              )}

              {selectedEmployee && (
                <div>
                  <button onClick={() => setSelectedEmployee(null)} style={styles.backLink}>
                    <span>&#8592;</span> {lang === "de" ? "Zuruck zum Team" : "Back to Team"}
                  </button>
                  <div style={styles.providerCard}>
                    <div style={styles.field}>
                      <label style={styles.label}>{t("name")}</label>
                      <div style={styles.readField}>{selectedEmployee.name}</div>
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>{t("email")}</label>
                      <div style={styles.readField}>{selectedEmployee.email}</div>
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>{lang === "de" ? "Telefonnummer" : "Phone Number"}</label>
                      <div style={styles.readField}>{selectedEmployee.phone || "-"}</div>
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>{lang === "de" ? "Qualifikation" : "Qualification"}</label>
                      <div style={styles.readField}>{qualificationLabel(selectedEmployee.qualification)}</div>
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>{lang === "de" ? "Beigetreten am" : "Joined On"}</label>
                      <div style={styles.readField}>{selectedEmployee.createdAt ? selectedEmployee.createdAt.split("T")[0] : "-"}</div>
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>{lang === "de" ? "Nicht verfugbare Tage" : "Unavailable Days"}</label>
                      <div style={styles.readField}>
                        {selectedEmployee.unavailableDates && selectedEmployee.unavailableDates.length > 0
                          ? selectedEmployee.unavailableDates.join(", ")
                          : (lang === "de" ? "Keine markiert" : "None marked")}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "profile" && isAgency && profileSection === "calendar" && (
            <div>
              <button onClick={() => setProfileSection(null)} style={styles.backLink}>
                <span>&#8592;</span> {lang === "de" ? "Zuruck" : "Back"}
              </button>

              <div style={styles.field}>
                <label style={styles.label}>{lang === "de" ? "Datum auswahlen" : "Select Date"}</label>
                <input type="date" value={calendarDate} onChange={(e) => setCalendarDate(e.target.value)} style={styles.input} />
              </div>

              {employees.length === 0 && (
                <div style={styles.emptyState}>
                  <p>{lang === "de" ? "Noch keine Mitarbeiter hinzugefugt." : "No employees added yet."}</p>
                </div>
              )}

              {employees.map((emp) => {
                const statusInfo = getEmployeeStatusForDate(emp, calendarDate);
                const dotColor = statusInfo.status === "free" ? "#2e7d32" : statusInfo.status === "assigned" ? "#1a5fb4" : "#c62828";
                return (
                  <div key={emp.id + "-cal"} style={styles.employeeRow}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: dotColor }} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{emp.name}</div>
                        <div style={{ fontSize: 11, color: "#888" }}>
                          {employeeStatusLabel(statusInfo.status)}
                          {statusInfo.status === "assigned" && statusInfo.patientName ? " - " + statusInfo.patientName : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={styles.bottomNav}>
          <button onClick={() => setActiveTab("home")} style={activeTab === "home" ? styles.navItemActive : styles.navItem}>
            <div style={styles.navIcon}>H</div>
            <div style={styles.navLabel}>{t("home")}</div>
          </button>
          <button onClick={() => setActiveTab("requests")} style={activeTab === "requests" ? styles.navItemActive : styles.navItem}>
            <div style={styles.navIcon}>A</div>
            <div style={styles.navLabel}>{lang === "de" ? "Anfragen" : "Requests"}</div>
          </button>
          <button onClick={() => setActiveTab("bookings")} style={activeTab === "bookings" ? styles.navItemActive : styles.navItem}>
            <div style={styles.navIcon}>B</div>
            <div style={styles.navLabel}>{t("bookings")}</div>
          </button>
          <button onClick={() => { setActiveTab("profile"); setProfileSection(null); }} style={activeTab === "profile" ? styles.navItemActive : styles.navItem}>
            <div style={styles.navIcon}>P</div>
            <div style={styles.navLabel}>{t("profile")}</div>
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  pageBackground: {
    minHeight: "100vh",
    background: "#dce6f2",
    display: "flex",
    justifyContent: "center",
    fontFamily: "'Segoe UI', sans-serif",
  },
  phoneFrame: {
    width: "100%",
    maxWidth: 420,
    minHeight: "100vh",
    background: "#f5f8fc",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 0 40px rgba(0,0,0,0.12)",
  },
  header: {
    background: "linear-gradient(135deg, #1a5fb4, #2f7fd6)",
    color: "#fff",
    padding: "20px 22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerTitle: { fontSize: 13, opacity: 0.85 },
  headerSubtitle: { fontSize: 19, fontWeight: 700, marginTop: 2 },
  langToggle: {
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    border: "none",
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  logoutBtn: {
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    border: "none",
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
  },
  content: {
    flex: 1,
    padding: "18px",
    paddingBottom: 100,
    boxSizing: "border-box",
  },
  noticeCard: {
    borderRadius: 12,
    padding: 14,
    marginBottom: 18,
  },
  noticeTitle: { fontWeight: 700, fontSize: 13 },
  sectionLabel: { fontSize: 12, color: "#888", fontWeight: 700, letterSpacing: 0.5, marginBottom: 10 },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginBottom: 18,
  },
  statCard: {
    background: "#fff",
    borderRadius: 14,
    padding: 16,
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  },
  statNumber: { fontSize: 24, fontWeight: 700, color: "#1a5fb4" },
  statLabel: { fontSize: 11, color: "#777", marginTop: 4 },
  field: { marginBottom: 14 },
  label: { display: "block", fontSize: 12, color: "#333", marginBottom: 6, fontWeight: 600 },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 13,
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  message: { fontSize: 12, color: "#1a5fb4", marginBottom: 8 },
  error: { color: "#d32f2f", fontSize: 12, marginBottom: 8 },
  hint: { fontSize: 12, color: "#777", marginBottom: 14 },
  button: {
    width: "100%",
    padding: "12px",
    background: "#1a5fb4",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 6,
  },
  acceptButton: {
    flex: 1,
    padding: "8px",
    background: "#2e7d32",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  declineButton: {
    flex: 1,
    padding: "8px",
    background: "#c62828",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  emptyState: { textAlign: "center", color: "#999", padding: "50px 0" },
  bottomNav: {
    background: "#fff",
    borderTop: "1px solid #eee",
    display: "flex",
    justifyContent: "space-around",
    padding: "10px 0",
  },
  navItem: {
    background: "none",
    border: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    color: "#999",
    cursor: "pointer",
    fontSize: 11,
  },
  navItemActive: {
    background: "none",
    border: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    color: "#1a5fb4",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 700,
  },
  navIcon: {
    fontSize: 14,
    fontWeight: 700,
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "#eee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  navLabel: { fontSize: 10 },
  pillRow: { display: "flex", gap: 8, marginBottom: 18 },
  pill: {
    flex: 1,
    padding: "8px 6px",
    borderRadius: 20,
    border: "1px solid #ddd",
    background: "#fff",
    color: "#555",
    fontSize: 12,
    cursor: "pointer",
  },
  pillActive: {
    flex: 1,
    padding: "8px 6px",
    borderRadius: 20,
    border: "1px solid #1a5fb4",
    background: "#1a5fb4",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  providerCard: {
    background: "#fff",
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  },
  providerType: { fontWeight: 700, fontSize: 13, color: "#222" },
  providerArea: { fontSize: 12, color: "#888" },
  providerBio: { fontSize: 13, color: "#555", marginTop: 6 },
  employeeRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#fff",
    padding: "10px 14px",
    borderRadius: 10,
    marginBottom: 8,
  },
  assignButton: {
    padding: "6px 12px",
    background: "#1a5fb4",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  assignButtonDisabled: {
    padding: "6px 12px",
    background: "#ddd",
    color: "#999",
    border: "none",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "not-allowed",
  },
  backLink: {
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 10,
    color: "#1a5fb4",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 6,
    marginBottom: 14,
    padding: "8px 14px",
    display: "flex",
    alignItems: "center",
    gap: 6,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  readField: {
    padding: "10px 12px",
    borderRadius: 8,
    background: "#f5f5f5",
    fontSize: 13,
    color: "#333",
  },
  menuCard: {
    background: "#fff",
    borderRadius: 14,
    padding: "16px 18px",
    marginBottom: 10,
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  },
  menuTitle: { fontWeight: 600, fontSize: 14, color: "#222" },
  menuArrow: { color: "#1a5fb4", fontWeight: 700 },
  patientContactBox: {
    background: "#f0f6ff",
    borderRadius: 8,
    padding: "8px 12px",
    marginTop: 8,
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  patientPhoto: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid #1a5fb4",
  },
};

export default ProviderDashboard;
